import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import { Badge, Button, FormField, Input } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import {
  calendarDateInputToIso,
  formatCalendarDate,
  isoDateInTz,
  useDateFormatter,
  useUserTimeZone,
} from "@/utils/datetime";
import {
  brokerageAffiliationsApi,
  AFFILIATION_TYPES,
  type AffiliationType,
  type BrokerageAffiliationDto,
  type PartnerKind,
} from "@/api/brokerages";
import type { BrokerType } from "@/api/brokers";
import { BrokerConversionModal } from "@/components/brokers/BrokerConversionModal";
import { BrokeragePicker, type BrokeragePickerResult } from "./BrokeragePicker";

interface BrokerageAffiliationsPanelProps {
  partnerId: string;
  partnerKind: PartnerKind;
  /** Broker money core — for a Broker partner, "Link to brokerage" drives the
   *  shared Independent↔Junior conversion component (not the raw affiliation
   *  link). These feed its copy + type preselect. A Lead partner has no broker
   *  type, so its "Link to brokerage" keeps the raw affiliation link. */
  partnerName?: string;
  brokerType?: BrokerType;
  /** Extra classes for the card shell so the parent grid can control width
   *  (FixBatch-06 FIND-35 — `md:col-span-3` makes this card match the
   *  COMPANY / ADDITIONAL CONTACTS cards on the broker detail page). */
  className?: string;
}

const TYPE_BADGE: Record<AffiliationType, "info" | "gold" | "draft" | "archived"> = {
  Employed: "gold",
  Contracted: "info",
  Affiliated: "draft",
  Unknown: "archived",
};

/**
 * Brokerage Module v1.0 — lists a Broker's or Lead's affiliations (with an
 * "include ended" toggle) and exposes per-row Edit / Set primary / End /
 * Remove actions plus a "Link to brokerage" affordance. Mutating actions are
 * gated on <c>Brokerages.Update</c>.
 */
export function BrokerageAffiliationsPanel({ partnerId, partnerKind, partnerName, brokerType, className = "" }: BrokerageAffiliationsPanelProps) {
  const { hasPermission } = useAuth();
  const dialogs = useDialogs();
  const { date } = useDateFormatter();
  const qc = useQueryClient();
  const canEdit = hasPermission("Brokerages.Update");

  const [includeEnded, setIncludeEnded] = useState(false);
  const [linking, setLinking] = useState(false);
  const [editing, setEditing] = useState<BrokerageAffiliationDto | null>(null);
  const [ending, setEnding] = useState<BrokerageAffiliationDto | null>(null);

  const queryKey = ["affiliations", partnerKind, partnerId, includeEnded];
  const list = useQuery({
    queryKey,
    queryFn: () => brokerageAffiliationsApi.listForPartner(partnerId, partnerKind, includeEnded),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["affiliations", partnerKind, partnerId] });

  const setPrimary = useMutation({
    mutationFn: (id: string) => brokerageAffiliationsApi.setPrimary(id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => brokerageAffiliationsApi.remove(id),
    onSuccess: invalidate,
  });

  return (
    <section
      className={`bg-white-pure border border-border-light rounded-lg p-space-4 ${className}`.trim()}
      data-testid="brokerage-affiliations-panel"
    >
      <div className="flex items-center justify-between mb-space-2">
        <h3 className="font-display font-bold text-small uppercase tracking-wider text-gold-shadow flex items-center gap-space-1">
          <Building2 size={14} strokeWidth={1.5} aria-hidden /> Brokerages
        </h3>
        {canEdit && (
          <Button size="sm" variant="secondary" onClick={() => setLinking(true)}>
            <Plus size={14} strokeWidth={1.5} aria-hidden /> Link to brokerage
          </Button>
        )}
      </div>

      <label className="flex items-center gap-space-1 text-caption text-gray-mid mb-space-2">
        <input
          type="checkbox"
          checked={includeEnded}
          onChange={(e) => setIncludeEnded(e.target.checked)}
        />
        Include ended affiliations
      </label>

      {list.isLoading && <p className="text-caption text-gray-mid">Loading…</p>}
      {list.isError && (
        <p className="text-caption text-status-error" role="alert">
          {(list.error as Error).message}
        </p>
      )}
      {list.data && list.data.length === 0 && (
        <p className="text-caption text-gray-mid">No brokerage affiliations.</p>
      )}

      <ul className="grid gap-space-2">
        {list.data?.map((a) => (
          <li
            key={a.id}
            className="border border-border-light rounded-md p-space-2 flex items-start justify-between gap-space-2"
            data-testid="affiliation-row"
          >
            <div className="min-w-0">
              <div className="flex items-center gap-space-1 flex-wrap">
                {a.isPrimary && (
                  <Star size={12} strokeWidth={1.5} aria-label="Primary" className="text-gold" fill="currentColor" />
                )}
                <Link to={`/brokerages/${a.brokerageId}`} className="font-semibold text-small hover:text-gold-shadow truncate">
                  {a.brokerageName}
                </Link>
                <Badge status={TYPE_BADGE[a.affiliationType]}>{a.affiliationType}</Badge>
                {a.endDate && <Badge status="archived">Ended</Badge>}
              </div>
              <div className="text-caption text-gray-mid mt-[2px]">
                {a.startDate ? date(a.startDate) : "—"}
                {/* An affiliation range is wall-calendar: `EndDate` is a day a
                    staff member picks. It is a MIXED population — the supersede
                    paths write `now` — but a date range should render as
                    calendar days either way, and legacy rows are stored at UTC
                    midnight, which the tz-aware `date()` showed a day early. */}
                {a.endDate ? ` → ${formatCalendarDate(a.endDate)}` : " → present"}
              </div>
            </div>

            {canEdit && (
              <div className="flex items-center gap-space-1 shrink-0">
                {!a.isPrimary && !a.endDate && (
                  <Button
                    size="sm"
                    variant="text"
                    aria-label="Set primary"
                    disabled={setPrimary.isPending}
                    onClick={() => setPrimary.mutate(a.id)}
                  >
                    <Star size={14} strokeWidth={1.5} aria-hidden />
                  </Button>
                )}
                <Button size="sm" variant="text" aria-label="Edit affiliation" onClick={() => setEditing(a)}>
                  <Pencil size={14} strokeWidth={1.5} aria-hidden />
                </Button>
                {!a.endDate && (
                  <Button size="sm" variant="text" aria-label="End affiliation" onClick={() => setEnding(a)}>
                    <X size={14} strokeWidth={1.5} aria-hidden />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="text"
                  aria-label="Remove affiliation"
                  disabled={remove.isPending}
                  onClick={async () => {
                    const ok = await dialogs.confirm({
                      title: "Remove affiliation",
                      message: "Remove this affiliation? Use this only when it was logged in error (ending a relationship uses “End affiliation” instead).",
                      confirmLabel: "Remove",
                      destructive: true,
                    });
                    if (ok) remove.mutate(a.id);
                  }}
                >
                  <Trash2 size={14} strokeWidth={1.5} aria-hidden />
                </Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {linking && (partnerKind === "Broker" ? (
        // Broker money core — a Broker partner's "Link to brokerage" drives the
        // shared conversion component (type + covering firm + per-deal absorb),
        // never the raw tracking-only link.
        <BrokerConversionModal
          brokerId={partnerId}
          brokerName={partnerName ?? partnerId.slice(0, 8)}
          currentType={brokerType}
          onClose={() => setLinking(false)}
          onConverted={() => { setLinking(false); invalidate(); }}
        />
      ) : (
        // A Lead partner has no broker type — keep the raw affiliation link.
        <LinkAffiliationModal
          partnerId={partnerId}
          partnerKind={partnerKind}
          onClose={() => setLinking(false)}
          onLinked={() => { setLinking(false); invalidate(); }}
        />
      ))}
      {editing && (
        <EditAffiliationModal
          affiliation={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); invalidate(); }}
        />
      )}
      {ending && (
        <EndAffiliationModal
          affiliation={ending}
          onClose={() => setEnding(null)}
          onEnded={() => { setEnding(null); invalidate(); }}
        />
      )}
    </section>
  );
}

function LinkAffiliationModal({
  partnerId,
  partnerKind,
  onClose,
  onLinked,
}: {
  partnerId: string;
  partnerKind: PartnerKind;
  onClose: () => void;
  onLinked: () => void;
}) {
  const [selected, setSelected] = useState<BrokeragePickerResult | null>(null);
  const [affiliationType, setAffiliationType] = useState<AffiliationType>("Affiliated");
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const link = useMutation({
    mutationFn: () =>
      brokerageAffiliationsApi.link({
        brokerageId: selected!.brokerageId,
        partnerId,
        partnerKind,
        affiliationType,
        isPrimary,
      }),
    onSuccess: onLinked,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Link to brokerage"
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" loading={link.isPending} disabled={!selected} onClick={() => link.mutate()}>
            Link
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3">
        <FormField label="Brokerage" required helperText={selected ? `Selected: ${selected.brokerageName}` : undefined}>
          <BrokeragePicker autoFocus onSelect={setSelected} />
        </FormField>
        <FormField label="Affiliation type">
          <Select value={affiliationType} onChange={(e) => setAffiliationType(e.target.value as AffiliationType)}>
            {AFFILIATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormField>
        <label className="flex items-center gap-space-2 text-small">
          <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
          Set as primary brokerage
        </label>
        {error && <p className="text-label text-status-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

function EditAffiliationModal({
  affiliation,
  onClose,
  onSaved,
}: {
  affiliation: BrokerageAffiliationDto;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [affiliationType, setAffiliationType] = useState<AffiliationType>(affiliation.affiliationType);
  const [isPrimary, setIsPrimary] = useState(affiliation.isPrimary);
  const [notes, setNotes] = useState(affiliation.notes ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      brokerageAffiliationsApi.update(affiliation.id, {
        affiliationType,
        isPrimary,
        notes: notes.trim() || null,
      }),
    onSuccess: onSaved,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit affiliation — ${affiliation.brokerageName}`}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
        </div>
      }
    >
      <div className="grid gap-space-3">
        <FormField label="Affiliation type">
          <Select value={affiliationType} onChange={(e) => setAffiliationType(e.target.value as AffiliationType)}>
            {AFFILIATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </FormField>
        <label className="flex items-center gap-space-2 text-small">
          <input type="checkbox" checked={isPrimary} onChange={(e) => setIsPrimary(e.target.checked)} />
          Primary brokerage
        </label>
        <FormField label="Notes">
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
        {error && <p className="text-label text-status-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}

function EndAffiliationModal({
  affiliation,
  onClose,
  onEnded,
}: {
  affiliation: BrokerageAffiliationDto;
  onClose: () => void;
  onEnded: () => void;
}) {
  const tz = useUserTimeZone();
  const [endDate, setEndDate] = useState(() => isoDateInTz(new Date(), tz));
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  const end = useMutation({
    mutationFn: () =>
      brokerageAffiliationsApi.end(affiliation.id, {
        endDate: calendarDateInputToIso(endDate)!,
        reason: reason.trim() || null,
      }),
    onSuccess: onEnded,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`End affiliation — ${affiliation.brokerageName}`}
      size="sm"
      footer={
        <div className="flex justify-end gap-space-2">
          <Button variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" loading={end.isPending} disabled={!endDate} onClick={() => end.mutate()}>
            End affiliation
          </Button>
        </div>
      }
    >
      <div className="grid gap-space-3">
        <p className="text-caption text-gray-mid">
          Ending the relationship keeps the row on the timeline (it is not deleted).
        </p>
        <FormField label="End date" required>
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </FormField>
        <FormField label="Reason">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional" />
        </FormField>
        {error && <p className="text-label text-status-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
}
